import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  AlertSchema,
  ServiceTemplate,
  Status,
  SystemAlertSchema,
} from "./types";
import {
  faFire,
  faHeartPulse,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import sentryImage from "./assets/alert-providers/sentry.png";
import { Schemas } from "./schemas";

export const Usd = {
  id: "usd",
  name: "US Dollar",
  symbol: "USD",
  currencyType: "fiat" as const,
  price: 1,
};

export const CURRENCY_IDS = {
  usd: "usd" as const,
  eur: "eur" as const,
  bitcoin: "bitcoin" as const,
  ethereum: "ethereum" as const,
};

export const SECONDS_IN_YEAR = 31536000;
export const SECONDS_IN_DAY = 86400;

export const SUGGESTED_CURRENCY_IDS = [
  CURRENCY_IDS.usd,
  CURRENCY_IDS.eur,
  CURRENCY_IDS.bitcoin,
  CURRENCY_IDS.ethereum,
];

export const KNOWLEDGE_BASE_URL = "https://kb.reactive.finance";
export const TELEGRAM_INVITE_URL = "https://t.me/ReactiveFi";
export const DISCORD_INVITE_URL = "https://discord.gg/rggaRkP8";
export const TWITTER_URL = "https://x.com/ReactiveFi";
export const GAS_NECESSARY_TO_COMPLETE_TX = 0.51;

export const SMALL_DIALOG_BACKGROUND_FILTER = {
  zIndex: -5,
  position: "absolute",
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  margin: "auto",
  width: "373px",
  height: "759px",
  borderRadius: "759px",
  backgroundColor: "colors.grey.900",
  filter: "blur(120px)",
  content: '""',
} as const;

export const LARGE_DIALOG_BACKGROUND_FILTER = {
  zIndex: -5,
  position: "absolute",
  left: 0,
  right: 0,
  top: -300,
  bottom: 0,
  margin: "auto",
  width: "697px",
  height: "921px",
  transform: "rotate(90deg)",
  borderRadius: "921px",
  filter: "blur(168px)",
  backgroundColor: "colors.grey.900",
  content: '""',
} as const;

export const DELAY_BEFORE_SHOWING_TOUR_POPUP_MS = 500;

export const TOUR_POPUP_FADE_IN_DURATION_MS = 500;

export const SIDEBAR_WIDTH_PX = 280;
export const SIDEBAR_COLLAPSED_WIDTH_PX = 72;

export const PERMANENT_NAVBAR_HEIGHT_PX = 81;
export const MOBILE_NAVBAR_HEIGHT_PX = 71;

export const TABLE_ROW_HEIGHT = { DESKTOP: 188, MOBILE: 188 };

export const OPTIMISTIC_RESPONSE_ACTIONS = {
  DUPLICATING: "DUPLICATING",
} as const;

export const DIALOG_WIDTH = {
  sm: "480px",
  md: "564px",
  lg: "500px",
  xl: "900px",
};

export const STATUS_COLOR: Record<Status, { main: string; subtle: string }> = {
  healthy: { main: "#22c55e", subtle: "rgb(205, 235, 217)" },
  warning: { main: "#f59e0b", subtle: "rgb(240, 211, 161)" },
  critical: { main: "#ef4444", subtle: "rgb(238, 177, 177)" },
};

export const STATUS_ICON: Record<Status, IconProp> = {
  healthy: faHeartPulse,
  warning: faTriangleExclamation,
  critical: faFire,
};

export const STATUS_SCORE: Record<Status, number> = {
  healthy: 0,
  warning: 2,
  critical: 3,
};

export const QUERY_PARAMS = {
  serviceId: "service_id",
};

export const LS_KEYS = {
  version: "v1.5",
  alertEvents: "alertEvents",
  dashboards: "dashboards",
  alerts: "alerts",
};

export const FORM_HELPER_SECTION_HEIGHT_PX = 26;

export const ACCOUNT_BUTTON_WIDTH_PX = 200;

export const DEFAULT_TOOLTIP_ENTER_DELAY_MS = 1500;

export const SYSTEM_ALERT_SCHEMAS = {
  ["system-coralogix"]: Schemas.alertSchema.parse({
    id: "system-coralogix",
    name: "Coralogix",
    foreignKeyProps: ["alert_id"],
    system: true,
    statusProp: "status",
    description: "Coralogix alert webhooks (templated alert_id).",
  }),
  ["system-datadog"]: Schemas.alertSchema.parse({
    id: "system-datadog",
    name: "Datadog",
    foreignKeyProps: ["alert_id"],
    system: true,
    statusProp: "status",
    description:
      "Datadog monitor webhooks (use alert_id as stable monitor identifier).",
  }),
  ["system-sentry"]: Schemas.alertSchema.parse({
    id: "system-sentry",
    name: "Sentry",
    foreignKeyProps: ["alert_id"],
    system: true,
    statusProp: "action",
    description: "Sentry issue-based webhooks (dedupe by issue_id/group id).",
  }),
  /*["system-pagerduty"]: Schemas.alertSchema.parse({
    id: "system-pagerduty",
    name: "PagerDuty",
    deduplicationKeyProps: ["incident_id"],
    system: true,
    statusProp: "status",
    description: "PagerDuty incident webhooks (dedupe by incident_id).",
  }),
  ["system-opsgenie"]: Schemas.alertSchema.parse({
    id: "system-opsgenie",
    name: "Opsgenie",
    deduplicationKeyProps: ["alert_id"],
    system: true,
    statusProp: "status",
    description: "Opsgenie alert webhooks (dedupe by alert_id).",
  }),
  "system-newrelic": Schemas.alertSchema.parse({
    id: "system-newrelic",
    name: "New Relic",
    deduplicationKeyProps: ["incident_id"],
    system: true,
    statusProp: "status",
    description: "New Relic alert notifications (dedupe by incident_id).",
  }),
  ["system-prometheus-alertmanager"]: Schemas.alertSchema.parse({
    id: "system-prometheus-alertmanager",
    name: "Prometheus Alertmanager",
    deduplicationKeyProps: ["alertname", "labels"],
    system: true,
    statusProp: "status",
    description: "Prometheus Alertmanager (dedupe by alertname + labels).",
  }),
  ["system-grafana"]: Schemas.alertSchema.parse({
    id: "system-grafana",
    name: "Grafana",
    deduplicationKeyProps: ["rule_uid"],
    system: true,
    statusProp: "status",
    description: "Grafana alerting webhooks (dedupe by rule_uid).",
  }),
  ["system-splunk"]: Schemas.alertSchema.parse({
    id: "system-splunk",
    name: "Splunk",
    deduplicationKeyProps: ["savedsearch_name"],
    system: true,
    statusProp: "status",
    description: "Splunk alert actions (dedupe by savedsearch_name).",
  }),
  ["system-cloudwatch"]: Schemas.alertSchema.parse({
    id: "system-cloudwatch",
    name: "AWS CloudWatch",
    deduplicationKeyProps: ["alarm_name"],
    system: true,
    statusProp: "status",
    description: "AWS CloudWatch alarms (dedupe by alarm_name).",
  }),
  ["system-azure-monitor"]: Schemas.alertSchema.parse({
    id: "system-azure-monitor",
    name: "Azure Monitor",
    deduplicationKeyProps: ["alert_rule_id"],
    system: true,
    statusProp: "status",
    description: "Azure Monitor alerts (dedupe by alert_rule_id).",
  }),
  ["system-gcp-monitoring"]: Schemas.alertSchema.parse({
    id: "system-gcp-monitoring",
    name: "GCP Monitoring",
    deduplicationKeyProps: ["incident_id"],
    system: true,
    statusProp: "status",
    description: "Google Cloud Monitoring alerts (dedupe by incident_id).",
  }),*/
} as const satisfies Record<string, AlertSchema>;

export const SYSTEM_ALERT_SCHEMAS_LOGO: Record<
  SystemAlertSchema,
  string | undefined
> = {
  "system-newrelic": undefined,
  "system-prometheus-alertmanager": undefined,
  "system-grafana": undefined,
  "system-splunk": undefined,
  "system-cloudwatch": undefined,
  "system-azure-monitor": undefined,
  "system-gcp-monitoring": undefined,
  "system-coralogix": undefined,
  "system-datadog": undefined,
  "system-sentry": sentryImage,
  "system-pagerduty": undefined,
  "system-opsgenie": undefined,
};

export const KPIs = {
  transaction_success_rate: Schemas.kpi.parse({
    name: "Transaction Success Rate",
    id: "transaction_success_rate",
    system: true,
    description: "Tracks the percentage of transactions completed successfully end-to-end.",
    thresholds: {
      warning: 50,
      critical: 50
    },
    metrics: {}
  }),
  checkout_latency_p95: Schemas.kpi.parse({
    name: "Checkout Latency P95",
    id: "checkout_latency_p95",
    system: true,
    description: "Measures 95th percentile checkout response time to detect user-perceived slowdowns.",
    thresholds: {
      warning: 50,
      critical: 50
    },
    metrics: {}   
  }),
  cart_abandonment_rate: Schemas.kpi.parse({
    name: "Cart Abandonment Rate",
    id: "cart_abandonment_rate",
    system: true,
    description: "Monitors the share of sessions where users leave before completing purchase.",
    thresholds: {
      warning: 50,
      critical: 50
    },
    metrics: {}   
  }),
  payment_processing_time: Schemas.kpi.parse({
    name: "Payment Processing Time",
    id: "payment_processing_time",
    description: "Measures processing latency for payment requests across the payment pipeline.",
    system: true,
    thresholds: {
      warning: 50,
      critical: 50
    },
    metrics: {}   
  }),
  login_success_rate: Schemas.kpi.parse({
    name: "Login Success Rate",
    id: "login_success_rate",
    system: true,
    description: "Tracks successful authentication attempts versus total login attempts.",
    thresholds: {
      warning: 50,
      critical: 50
    },
    metrics: {}   
  }),
  token_validation_latency: Schemas.kpi.parse({
    name: "Token Validation Latency",
    id: "token_validation_latency",
    system: true,
    description: "Monitors latency for token validation checks in auth and gateway paths.",
    thresholds: {
      warning: 50,
      critical: 50
    },
    metrics: {}   
  }),
  session_management: Schemas.kpi.parse({
    name: "Session Management",
    id: "session_management",
    system: true,
    description: "Tracks reliability and performance of session creation, refresh, and invalidation.",
    thresholds: {
      warning: 50,
      critical: 50
    },
    metrics: {}
  })
} as const;

export const SERVICE_TEMPLATES = {
  saas: {
    id: "saas",
    name: "SaaS Application",
    system: true,
    kpis: [KPIs.session_management, KPIs.token_validation_latency],
    description:
      "Standard SaaS application with API, web frontend, and database tiers",
    type: "saas"
  },
  retail: {
    id: "retail",
    kpis: [KPIs.cart_abandonment_rate, KPIs.payment_processing_time, KPIs.token_validation_latency],
    system: true,
    name: "Retail Checkout",
    description:
      "E-commerce checkout flow including cart, payment, and inventory systems",
    type: "retail",
  },
  banking: {
    id: "banking",
    kpis: [KPIs.login_success_rate, KPIs.checkout_latency_p95],
    system: true,
    name: "Banking payments",
    description:
      "Financial payment processing with compliance and fraud detection",
    type: "banking",
  },
  internalit: {
    id: "internalit",
    kpis: [KPIs.transaction_success_rate],
    system: true,
    name: "Internal IT",
    description: "Internal IT service management with ITSM integration",
    type: "internal_it",
  }
} as const satisfies Record<string, ServiceTemplate>;
